import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useMerchantStore } from '@/stores/merchant';

const Login = () => import('@/views/Login.vue');
const MfaChallenge = () => import('@/views/MfaChallenge.vue');
const MfaBootstrap = () => import('@/views/MfaBootstrap.vue');
const ForcedPasswordChange = () => import('@/views/ForcedPasswordChange.vue');
const Dashboard = () => import('@/views/Dashboard.vue');
const NoAccess = () => import('@/views/NoAccess.vue');
const Applications = () => import('@/views/applications/Applications.vue');
const NewApplication = () => import('@/views/applications/NewApplication.vue');
const SettingsApiKeys = () => import('@/views/settings/ApiKeys.vue');
const SettingsWebhooks = () => import('@/views/settings/Webhooks.vue');
const SettingsThemeDemo = () => import('@/views/settings/ThemeDemo.vue');
const SettingsAccount = () => import('@/views/settings/Account.vue');
const SettingsMfa = () => import('@/views/settings/MfaSettings.vue');
const SettingsSecurity = () => import('@/views/settings/Security.vue');
const Agreements = () => import('@/views/agreements/Agreements.vue');
const AgreementDetail = () => import('@/views/agreements/AgreementDetail.vue');
const Reports = () => import('@/views/reports/Reports.vue');

const routes = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresGuest: true },
  },
  {
    path: '/mfa/challenge',
    name: 'MfaChallenge',
    component: MfaChallenge,
    meta: { requiresGuest: true },
  },
  {
    path: '/mfa/setup',
    name: 'MfaBootstrap',
    component: MfaBootstrap,
    meta: { requiresGuest: true },
  },
  {
    // Forced password change (PCI DSS 8.3.5, #1198). Deliberately carries
    // NEITHER requiresGuest NOR requiresAuth: it is reachable both pre-auth
    // (holding a short-lived change token from the login/MFA step) and from an
    // authenticated-but-gated session whose token predates the flag.
    // requiresGuest would bounce the second case to the dashboard, which the
    // RPC middleware then refuses to render — the definition of bricked. The
    // view itself sends anyone with neither state back to /login.
    path: '/password/change-required',
    name: 'ForcedPasswordChange',
    component: ForcedPasswordChange,
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: { requiresAuth: true, requiresMerchant: true },
  },
  {
    path: '/applications',
    name: 'Applications',
    component: Applications,
    meta: { requiresAuth: true, requiresMerchant: true },
  },
  {
    path: '/applications/new',
    name: 'NewApplication',
    component: NewApplication,
    meta: { requiresAuth: true, requiresMerchant: true },
  },
  {
    path: '/agreements',
    name: 'Agreements',
    component: Agreements,
    meta: { requiresAuth: true, requiresMerchant: true },
  },
  {
    path: '/agreements/:id',
    name: 'AgreementDetail',
    component: AgreementDetail,
    meta: { requiresAuth: true, requiresMerchant: true },
  },
  {
    path: '/payouts',
    name: 'Payouts',
    component: Dashboard, // Placeholder - will be replaced
    meta: { requiresAuth: true, requiresMerchant: true },
  },
  {
    path: '/reports',
    name: 'Reports',
    component: Reports,
    meta: { requiresAuth: true, requiresMerchant: true },
  },
  {
    path: '/settings',
    redirect: '/settings/account',
  },
  {
    path: '/settings/account',
    name: 'SettingsAccount',
    component: SettingsAccount,
    // No requiresMerchant: even a user with no merchants can change their password.
    meta: { requiresAuth: true },
  },
  {
    path: '/settings/mfa',
    name: 'SettingsMfa',
    component: SettingsMfa,
    // No requiresMerchant: account security applies regardless of merchant scope.
    meta: { requiresAuth: true },
  },
  {
    path: '/settings/security',
    name: 'SettingsSecurity',
    component: SettingsSecurity,
    meta: { requiresAuth: true, requiresMerchant: true },
  },
  {
    path: '/settings/api-keys',
    name: 'SettingsApiKeys',
    component: SettingsApiKeys,
    meta: { requiresAuth: true, requiresMerchant: true },
  },
  {
    path: '/settings/webhooks',
    name: 'SettingsWebhooks',
    component: SettingsWebhooks,
    meta: { requiresAuth: true, requiresMerchant: true },
  },
  {
    path: '/settings/theme',
    name: 'SettingsTheme',
    component: SettingsThemeDemo,
    meta: { requiresAuth: true, requiresMerchant: true },
  },
  {
    path: '/no-access',
    name: 'NoAccess',
    component: NoAccess,
    meta: { requiresAuth: true },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/dashboard',
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();
  const merchantStore = useMerchantStore();

  // Initialize auth if not already done
  if (!authStore.user && localStorage.getItem('hub_token')) {
    await authStore.initializeAuth();
  }

  // Check guest routes (login page)
  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    return next({ name: 'Dashboard' });
  }

  // Check auth routes
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return next({ name: 'Login', query: { redirect: to.fullPath } });
  }

  // Forced password change (PCI DSS 8.3.5, #1198). Checked BEFORE the merchant
  // gate: a gated session is refused by the RPC middleware everywhere, so any
  // other destination renders a shell the API will not fill (and the merchant
  // bootstrap call would 403 straight to /no-access). initializeAuth() has just
  // refreshed the flag from `auth@me` — the one method that middleware still
  // answers.
  if (
    authStore.isAuthenticated &&
    authStore.mustChangePassword &&
    to.name !== 'ForcedPasswordChange'
  ) {
    return next({ name: 'ForcedPasswordChange' });
  }

  // Check merchant access for routes that require it
  if (to.meta.requiresMerchant && authStore.isAuthenticated) {
    // Initialize merchant context if not done
    if (!merchantStore.isInitialized) {
      const success = await merchantStore.initializeMerchantContext();

      if (!success || !merchantStore.hasMerchantAccess) {
        return next({ name: 'NoAccess' });
      }
    } else if (!merchantStore.hasMerchantAccess) {
      return next({ name: 'NoAccess' });
    }
  }

  next();
});

export default router;
